import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    ColumnDef,
    flexRender,
    SortingState,
    PaginationState,
} from "@tanstack/react-table";
import { Image } from "react-bootstrap";
import { ASSESTS } from "../../../../../constant/assest";
import CustomButton, {
    ButtonFont,
    ButtonSize,
    ButtonType,
} from "../../../../../component/common/CustomButton/CustomButton";
import cn from "classnames";
import UserCard from "../../../../../component/common/UserCard/UserCard";
import React, { useEffect, useMemo, useState, useRef } from "react";
import Pagination from "../../../../../component/UI/Pagination/Pagination";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../../redux/store";
import { fetchProAccountList } from "../../../../../redux/slices/proAccounts/actions";
import { ProAccountListItem } from "../../../../../redux/slices/proAccounts/types";
import TableSkeleton from "../../../../../component/common/TableSkeleton";
import { useNavigate } from "react-router-dom";
import { usePagination } from "../../../../../hooks/usePagination";

interface SubscriptionOverviewTableProps {
    searchTerm: string;
    status: string;
    subsType: string;
    formattedStartDate?: string; 
    formattedEndDate?: string;   
}

const SubscriptionOverviewTable: React.FC<SubscriptionOverviewTableProps> = ({
    searchTerm,
    status,
    subsType,
    formattedStartDate,
    formattedEndDate    
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { proAccountList, proAccountListLoading } = useSelector(
        (state: RootState) => state.proAccounts
    );
    const processedList = useMemo(
        () =>
            proAccountList?.listing.map((user) => ({
                ...user,
                full_name: user.full_name ? user.full_name.trim() : "",
            })),
        [proAccountList?.listing]
    );
    const totalPages = proAccountList?.totalPages || 0;
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 5,
    });
    const [sorting, setSorting] = useState<SortingState>([]);

    const prevSearchTerm = useRef(searchTerm);
    const prevStatus = useRef(status);
    const prevSubsType = useRef(subsType);
    const prevFormattedStartDate = useRef(formattedStartDate); 
    const prevFormattedEndDate = useRef(formattedEndDate);     

    useEffect(() => {
        if (formattedStartDate && formattedEndDate) {
            const searchChanged = prevSearchTerm.current !== searchTerm;
            const statusChanged = prevStatus.current !== status;
            const subsTypeChanged = prevSubsType.current !== subsType;
            const startDateChanged = prevFormattedStartDate.current !== formattedStartDate; 
            const endDateChanged = prevFormattedEndDate.current !== formattedEndDate;     

            if ((searchChanged || statusChanged || subsTypeChanged || startDateChanged || endDateChanged) && pagination.pageIndex !== 0) {
                setPagination(old => ({ ...old, pageIndex: 0 }));
                return;
            }

            const params: any = {
                limit: pagination.pageSize,
                search: searchTerm || undefined,
                status: status || undefined,
                subsType: subsType || undefined,
                startDate: formattedStartDate || undefined, 
                endDate: formattedEndDate || undefined,     
            };
            if (!searchChanged && !statusChanged && !subsTypeChanged && !startDateChanged && !endDateChanged) {
                params.page = pagination.pageIndex + 1;
            }

            dispatch(fetchProAccountList(params));

            // Update refs for the next render.
            prevSearchTerm.current = searchTerm;
            prevStatus.current = status;
            prevSubsType.current = subsType;
            prevFormattedStartDate.current = formattedStartDate;
            prevFormattedEndDate.current = formattedEndDate;     
        }
    }, [dispatch, pagination, searchTerm, status, subsType, formattedStartDate, formattedEndDate]);

    const columns = useMemo<ColumnDef<ProAccountListItem>[]>(
        () => [
            {
                header: "User",
                accessorKey: "full_name",
                cell: ({ row }) => (
                    <UserCard
                        userName={row.original.full_name || 'N/A'}
                        userHandle={row.original.username}
                        userImage={row.original.profile_image}
                    />
                ),
                sortingFn: (rowA, rowB, columnId) => {
                    const a = rowA.getValue<string>(columnId) || '';
                    const b = rowB.getValue<string>(columnId) || '';
                    return a.localeCompare(b);
                },
            },
            {
                header: "Subscription",
                accessorFn: (row) => row.subscriptions?.plan?.plan_type,
                cell: (info) => {
                    const value = info.getValue<string>() || "N/A";                  
                    return (
                        <span className={`subscribeBadge ${value.toLowerCase()}`}>
                            {value}
                        </span>
                    );
                },
            },
            {
                header: "Plan Name",
                accessorFn: (row) => row.subscriptions?.plan?.plan_name || 'N/A',
            },
            {
                header: "Status",
                accessorFn: (row) => row.subscriptions?.status,
                cell: (info) => {
                    const value = info.getValue<string>() || "N/A";
                    return (
                        <span className={`statusBadge ${value.toLowerCase()}`}>
                            {value}
                        </span>
                    );
                },
            },
            {
                header: "Start Date",
                accessorFn: (row) => row.subscriptions?.start_date || "N/A",
            },
            {
                header: "End Date",
                accessorFn: (row) => row.subscriptions?.end_date || "N/A",
            },
            {
                header: "Amount",
                accessorFn: (row) => row.subscriptions?.amount_paid ?? null,
                cell: (info) => {
                    const row = info.row.original;
                    return row.subscriptions ? `${row.subscriptions.amount_paid} ${row.subscriptions.currency}` : "N/A";
                },
                sortingFn: (a, b, columnId) => {
                    const valA = a.getValue<number>(columnId);
                    const valB = b.getValue<number>(columnId);
                    if (valA == null) return 1;
                    if (valB == null) return -1;
                    return valA - valB;
                },
            },
            {
                header: "Payment Method",
                accessorFn: (row) => row.subscriptions?.payment_method || "N/A",
            },
        ],
        []
    );

    const table = useReactTable({
        data: processedList || [],
        columns,
        state: { pagination, sorting },
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        manualPagination: true,
        pageCount: totalPages,
    });

    const paginationGroup = usePagination({
        currentPageIndex: pagination.pageIndex,
        totalPages: totalPages,
      });
    
      const pageNumbersElements = paginationGroup.map((page, index) => {
        if (typeof page === 'string') {
          return <span key={`ellipsis-${index}`} className={'pageNumberBtn'} style={{cursor: 'default'}}>{page}</span>;
        }
    
        const pageIndex = page;
        return (
          <CustomButton
            key={pageIndex}
            onClick={() => table.setPageIndex(pageIndex)}
            disabled={pagination.pageIndex === pageIndex}
            btnType={
              pagination.pageIndex === pageIndex
                ? ButtonType.ACTIVENOSTYLE
                : ButtonType.NOSTYLE
            }
            btnSize={ButtonSize.NORMAL}
            btnFont={
              pagination.pageIndex === pageIndex
                ? ButtonFont.BOLD
                : ButtonFont.NORMAL
            }
            className={cn(
              'pageNumberBtn',
              pagination.pageIndex === pageIndex && 'activePageNumberBtn'
            )}
          >
            {pageIndex + 1}
          </CustomButton>
        );
      });
    
    return (
        <>
            <div className={'tableContainer'}>
                <div className={"tableWrapper"}>
                    <table>
                        <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => {
                                        return (
                                            <th
                                                key={header.id}
                                                onClick={header.column.getToggleSortingHandler()}
                                                style={{
                                                    cursor: header.column.getCanSort()
                                                        ? "pointer"
                                                        : "default",
                                                }}
                                                className={cn(
                                                    header.column.columnDef.id &&
                                                    `th-${header.column.columnDef.id}`
                                                )}
                                            >
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                                {header.column.getCanSort() && (
                                                    <span className={cn("tableShorting")}>
                                                        <Image
                                                            src={
                                                                header.column.getIsSorted() === "asc"
                                                                    ? ASSESTS.ASSENDING
                                                                    : header.column.getIsSorted() === "desc"
                                                                        ? ASSESTS.DECENDING
                                                                        : ASSESTS.SHORT
                                                            }
                                                            alt="Short"
                                                        />
                                                    </span>
                                                )}
                                            </th>
                                        );
                                    })}
                                </tr>
                            ))}
                        </thead>
                        <tbody className="table-row-clickable">
                            {proAccountListLoading === "pending" ? (
                                <TableSkeleton
                                    columns={columns.length}
                                    rows={pagination.pageSize}
                                />
                            ) : table.getRowModel().rows.length === 0 ? (
                                <tr>
                                    <td colSpan={columns.length} className={'noResultsFound'}>
                                        No results found
                                    </td>
                                </tr>
                            ) : (
                                table.getRowModel().rows.map((row) => (
                                        <tr key={row.id}
                                        onClick={() => navigate(`/pro-accounts/portfolios/${row.original.id}`)}
                                    >
                                        {row.getVisibleCells().map((cell) => {
                                            return (
                                                <td
                                                    key={cell.id}
                                                    className={cn(
                                                        cell.column.columnDef.id &&
                                                        `td-${cell.column.columnDef.id}`
                                                    )}
                                                >
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext()
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                <Pagination
                    enteriesShows={`${proAccountList?.total
                            ? pagination.pageIndex * pagination.pageSize + 1
                            : 0
                        }-${Math.min(
                            (pagination.pageIndex + 1) * pagination.pageSize,
                            proAccountList?.total || 0
                        )} of ${proAccountList?.total || 0} Entries`}

                    pageNumbers= {[
                        totalPages > 1 && (
                            <CustomButton
                                key="prev"
                                onClick={() => table.previousPage()}
                                disabled={!table.getCanPreviousPage()}
                                btnType={ButtonType.NOSTYLE}
                                className={'pageNumberBtn'}
                            >
                                Prev
                            </CustomButton>
                        ),
                        ...pageNumbersElements,
                        totalPages > 1 && (
                            <CustomButton
                                key="next"
                                onClick={() => table.nextPage()}
                                disabled={!table.getCanNextPage()}
                                btnType={ButtonType.NOSTYLE}
                                className={'pageNumberBtn'}
                            >
                                Next
                            </CustomButton>
                        )
                        ].filter(Boolean)}
                    dropDownOptions={[
                        { value: "5", label: "5" },
                        { value: "10", label: "10" },
                        { value: "20", label: "20" },
                        { value: "25", label: "25" },
                    ]}
                    dropdownOnChange={(value) => {
                        setPagination((old) => ({
                            ...old,
                            pageSize: Number(value),
                            pageIndex: 0,
                        }));
                    }}
                    dropDownPlaceholder={pagination.pageSize.toString()}
                />
            </div>
        </>
    );
};

export default React.memo(SubscriptionOverviewTable);
